import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { isProjectColumnAvailable } from "@/utils/projectFeatures";

// Shared per-user column visibility for BOTH the Master and Manager boards.
// Account-level (backend) is the source of truth so preferences follow the
// user across devices/browsers; localStorage is used only as an instant-paint
// offline cache. Preferences are separated per (tenant × user × board) on the
// server. Gated to Owner/Admin + Office/Admin by the caller passing `canHide`.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations/board-preferences`;

const cacheKey = (board, userId) => `board-cols:${board}:${userId || "anon"}`;

function readCache(board, userId, hideable) {
  try {
    const raw = window.localStorage.getItem(cacheKey(board, userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k) => hideable.includes(k)) : [];
  } catch {
    return [];
  }
}

function writeCache(board, userId, keys) {
  try {
    window.localStorage.setItem(cacheKey(board, userId), JSON.stringify(keys));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export function useColumnVisibility({ board, columns, canHide, userId, features = {} }) {
  // Hideable = every non-sticky column. The sticky column (Project) is locked.
  // `columns` is typically a fresh array each render, so derive a STABLE string
  // key from its hideable keys and memoize/dep on that (prevents render loops).
  const hideableKey = columns.filter(([, , sticky]) => !sticky).map(([key]) => key).join(",");
  const hideable = useMemo(() => (hideableKey ? hideableKey.split(",") : []), [hideableKey]);
  const [hidden, setHidden] = useState(() => new Set());
  const [defaultHidden, setDefaultHidden] = useState([]);
  const [open, setOpen] = useState(false);

  // Hydrate from cache immediately, then reconcile with the account (backend).
  useEffect(() => {
    if (!canHide || !userId) {
      setHidden(new Set());
      return undefined;
    }
    setHidden(new Set(readCache(board, userId, hideable)));
    let alive = true;
    axios
      .get(`${API}/${board}`)
      .then((res) => {
        if (!alive) return;
        const valid = (res.data?.hidden_columns || []).filter((k) => hideable.includes(k));
        setDefaultHidden((res.data?.default_hidden || []).filter((k) => hideable.includes(k)));
        setHidden(new Set(valid));
        writeCache(board, userId, valid);
      })
      .catch(() => {
        /* keep cache/default view on error — board stays usable */
      });
    return () => {
      alive = false;
    };
    // `hideable` is memoized on `hideableKey`, so its identity only changes when
    // the hideable column set changes — depending on it is exhaustive-deps-safe
    // and behaviorally identical to keying on `hideableKey` (no render loop).
  }, [board, userId, canHide, hideable]);

  const persist = useCallback(
    (next) => {
      const keys = Array.from(next);
      writeCache(board, userId, keys);
      axios.put(`${API}/${board}`, { hidden_columns: keys }).catch(() => {
        /* local cache retains the choice; will re-sync next load */
      });
    },
    [board, userId],
  );

  const toggle = useCallback(
    (key) => {
      if (!hideable.includes(key)) return; // Project can never be toggled
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        persist(next);
        return next;
      });
    },
    [hideable, persist],
  );

  const showAll = useCallback(() => {
    setHidden((previous) => {
      const next = new Set([...previous].filter((key) => !isProjectColumnAvailable(key, features)));
      persist(next);
      return next;
    });
  }, [persist, features]);

  const resetToDefault = useCallback(() => {
    setHidden((previous) => {
      const next = new Set([...previous].filter((key) => !isProjectColumnAvailable(key, features)));
      for (const key of defaultHidden) if (isProjectColumnAvailable(key, features)) next.add(key);
      persist(next);
      return next;
    });
  }, [persist, defaultHidden, features]);

  const visibleColumns = useMemo(
    () => columns.filter(([key]) => isProjectColumnAvailable(key, features) && !hidden.has(key)),
    [columns, hidden, features],
  );

  const isDefault = useMemo(() => {
    const def = new Set(defaultHidden.filter((key) => isProjectColumnAvailable(key, features)));
    const availableHidden = [...hidden].filter((key) => isProjectColumnAvailable(key, features));
    if (def.size !== availableHidden.length) return false;
    for (const k of availableHidden) if (!def.has(k)) return false;
    return true;
  }, [hidden, defaultHidden, features]);

  const effectiveHidden = new Set([...hidden, ...columns.filter(([key]) => !isProjectColumnAvailable(key, features)).map(([key]) => key)]);

  return {
    hidden: effectiveHidden,
    hideable: hideable.filter((key) => isProjectColumnAvailable(key, features)),
    availableColumns: columns.filter(([key]) => isProjectColumnAvailable(key, features)),
    hiddenCount: [...hidden].filter((key) => isProjectColumnAvailable(key, features)).length,
    isHidden: (key) => effectiveHidden.has(key),
    visibleColumns,
    open,
    setOpen,
    toggle,
    showAll,
    resetToDefault,
    isDefault,
    canHide: Boolean(canHide),
  };
}
